import { useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, CalendarClock, Menu, X, Globe } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const LANGS = [
  { id: "it", label: "IT" },
  { id: "en", label: "EN" },
  { id: "fr", label: "FR" },
  { id: "de", label: "DE" },
];

const LangToggle = ({ className = "" }) => {
  const { lang, setLang } = useLanguage();
  const [open, setOpen] = useState(false);
  return (
    <div className={`relative ${className}`}>
      <button
        onClick={() => setOpen(!open)}
        data-testid="lang-toggle"
        aria-label="Switch language"
        className="flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-2 text-xs font-semibold text-white/70 hover:border-neon-cyan/50 hover:text-neon-cyan transition-colors"
      >
        <Globe size={14} /> {lang.toUpperCase()}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 glass-panel rounded-xl overflow-hidden z-50" data-testid="lang-menu">
          {LANGS.map((l) => (
            <button
              key={l.id}
              onClick={() => { setLang(l.id); setOpen(false); }}
              data-testid={`lang-option-${l.id}`}
              className={`block w-full px-5 py-2.5 text-xs font-semibold text-left transition-colors ${lang === l.id ? "text-neon-cyan bg-white/5" : "text-white/60 hover:text-white hover:bg-white/5"}`}
            >
              {l.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Navbar = ({ onOpenDemo, waLink }) => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const home = pathname === "/";
  const linkHref = (h) => (!home && h.startsWith("#") ? `/${h}` : h);

  return (
    <header className="fixed top-0 inset-x-0 z-50 glass-panel border-x-0 border-t-0">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
        <a href={home ? "#top" : "/"} data-testid="nav-logo" className="flex flex-col leading-none select-none">
          <span className="font-display font-extrabold tracking-[0.12em] text-lg">
            <span className="neon-text">X</span><span className="text-white">SALES</span>
          </span>
          <span className="flex items-center gap-1.5 mt-1">
            <span className="h-px w-4 bg-gradient-to-r from-neon-cyan to-neon-violet" />
            <span className="text-[8px] tracking-[0.32em] neon-text font-semibold whitespace-nowrap">AI COMPANY BUILDER</span>
            <span className="h-px w-4 bg-gradient-to-r from-neon-violet to-neon-magenta" />
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-8 text-sm text-white/70">
          {t.nav.links.map((l) => (
            <a key={l.href} href={linkHref(l.href)} data-testid={`nav-link-${l.id}`} className="nav-link hover:text-neon-cyan transition-colors duration-300">
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <LangToggle />
          <a
            href={waLink(t.common.waDefault)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-whatsapp-button"
            className="flex items-center gap-2 rounded-full border border-neon-cyan/40 px-4 py-2 text-sm text-neon-cyan hover:bg-neon-cyan/10 transition-colors duration-300"
          >
            <MessageCircle size={16} /> {t.nav.whatsapp}
          </a>
          <button
            onClick={onOpenDemo}
            data-testid="nav-demo-button"
            className="flex items-center gap-2 rounded-full bg-gradient-to-r from-neon-cyan to-neon-violet px-4 py-2 text-sm font-semibold text-void hover:opacity-90 transition-opacity duration-300"
          >
            <CalendarClock size={16} /> {t.nav.demo}
          </button>
        </div>

        <button
          className="lg:hidden text-white p-2 -m-2"
          onClick={() => setOpen(!open)}
          data-testid="nav-menu-button"
          aria-label="Menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden glass-panel border-x-0 px-6 py-6 flex flex-col gap-4" data-testid="nav-mobile-menu">
          {t.nav.links.map((l) => (
            <a key={l.href} href={linkHref(l.href)} onClick={() => setOpen(false)} className="text-white/80 text-base">
              {l.label}
            </a>
          ))}
          <div className="flex flex-wrap gap-3 pt-2">
            <LangToggle />
            <a
              href={waLink(t.common.waDefault)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 rounded-full border border-neon-cyan/40 px-4 py-2 text-sm text-neon-cyan"
            >
              <MessageCircle size={16} /> {t.nav.whatsapp}
            </a>
            <button
              onClick={() => { setOpen(false); onOpenDemo(); }}
              className="flex items-center gap-2 rounded-full bg-gradient-to-r from-neon-cyan to-neon-violet px-4 py-2 text-sm font-semibold text-void"
            >
              <CalendarClock size={16} /> {t.nav.demo}
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
